import { useTranslation } from "react-i18next";
import {
  FiArrowDown,
  FiArrowUp,
  FiCpu,
  FiGlobe,
  FiMonitor,
  FiSmartphone,
  FiWifi,
} from "react-icons/fi";
import {
  getTopOverviewCompactTitle,
  getTopOverviewDisplayValue,
  getTopOverviewIconKind,
  getTopOverviewItemImage,
  getTopOverviewSectionItems,
  getVisibleTopOverviewSkeletonItems,
  shouldShowTopOverviewExperience,
  TOP_OVERVIEW_SECTIONS,
  useTopOverviewContainerWidth,
  useTopOverviewLayout,
  useUbiquitiTopOverview,
} from "./UbiquitiOverview.logic";

const TopOverviewIcon = ({ item, type }) => {
  const iconKind = getTopOverviewIconKind(item, type);

  if (iconKind === "image") {
    return <img src={getTopOverviewItemImage(item)} alt="" />;
  }

  if (iconKind === "wifi") {
    return <FiWifi />;
  }

  if (iconKind === "globe") {
    return <FiGlobe />;
  }

  if (iconKind === "smartphone") {
    return <FiSmartphone />;
  }

  if (iconKind === "monitor") {
    return <FiMonitor />;
  }

  return <FiCpu />;
};

const TopOverviewTooltip = ({ item, showExperience, type }) => (
  <div className="ubiquiti-top-tooltip" role="tooltip">
    <div className="ubiquiti-top-tooltip-title">
      <span className="ubiquiti-top-tooltip-icon">
        {type === "ap" ? (
          <i className="is-ap-icon"></i>
        ) : (
          <TopOverviewIcon item={item} type={type} />
        )}
      </span>
      <span>{getTopOverviewDisplayValue(item.title)}</span>
    </div>
    {item.periodDisplay ? (
      <small>{item.periodDisplay}</small>
    ) : null}
    <dl>
      <div>
        <dt>Download</dt>
        <dd className="is-download"><FiArrowDown /> {getTopOverviewDisplayValue(item.downloadDisplay)}</dd>
      </div>
      <div>
        <dt>Upload</dt>
        <dd className="is-upload"><FiArrowUp /> {getTopOverviewDisplayValue(item.uploadDisplay)}</dd>
      </div>
      <div>
        <dt>Traffic</dt>
        <dd>{getTopOverviewDisplayValue(item.trafficDisplay)}</dd>
      </div>
      {showExperience ? (
        <div>
          <dt>Experience</dt>
          <dd className="is-experience">{getTopOverviewDisplayValue(item.experienceDisplay)}</dd>
        </div>
      ) : null}
    </dl>
  </div>
);

const TopOverviewItem = ({ item, type }) => {
  const title = getTopOverviewDisplayValue(item.title);
  const compactTitle = getTopOverviewCompactTitle(title);
  const showExperience = shouldShowTopOverviewExperience(type);
  return (
    <div
      className={`ubiquiti-top-item is-${type}`}
      aria-label={title}
      tabIndex={0}
      title={title}
    >
      <span className="ubiquiti-top-icon">
        {type === "ap" ? (
          <i className="is-ap-icon"></i>
        ): (
            <TopOverviewIcon item = { item } type = { type } />
        )}
      </span>
      <span className="ubiquiti-top-label">{compactTitle}</span>
      <TopOverviewTooltip item={item} showExperience={showExperience} type={type} />
    </div>
  );
};

const TopOverviewSection = ({ containerWidth, isLoading, items, section }) => {
  const { t } = useTranslation();
  const skeletonItems = getVisibleTopOverviewSkeletonItems(section, containerWidth);

  return (
    <section className={`ubiquiti-top-section is-${section.type}`}>
      <div className="ubiquiti-top-title">{section.title}</div>
      <div className="ubiquiti-top-items">
        {isLoading ? skeletonItems.map((itemKey) => (
          <div className="ubiquiti-top-item is-loading" key={itemKey}>
            <span className="ubiquiti-top-icon"></span>
            <span className="ubiquiti-top-label"></span>
          </div>
        )) : items.length > 0 ? items.map((item) => (
          <TopOverviewItem item={item} type={section.type} key={item.id || item.title} />
        )) : (
          <div className="ubiquiti-empty-inline">{t("No data available")}</div>
        )}
      </div>
    </section>
  );
};

const UbiquitiTopOverview = ({ apiUrl, enabled = true, onQueryStateChange, refreshKey = 0, siteId }) => {
  const { isInitialLoading, overview } = useUbiquitiTopOverview({
    apiUrl,
    enabled,
    onQueryStateChange,
    refreshKey,
    siteId,
  });
  const { containerRef, containerWidth } = useTopOverviewContainerWidth();
  const { visibleOverview, isConstrained } = useTopOverviewLayout(overview, containerWidth);

  return (
    <article
      className={`ubiquiti-top-overview${isConstrained ? " is-constrained" : ""}`}
      ref={containerRef}
    >
      {TOP_OVERVIEW_SECTIONS.map((section) => (
        <TopOverviewSection
          containerWidth={containerWidth}
          isLoading={isInitialLoading}
          items={getTopOverviewSectionItems(visibleOverview, section.key)}
          section={section}
          key={section.key}
        />
      ))}
    </article>
  );
};

export default UbiquitiTopOverview;
